/**
 * PriceChart.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Root chart component — state, callbacks, JSX wiring only.
 *
 * Architecture rules:
 *   - ZERO D3 imports
 *   - ZERO direct fetch for chart data (lives in useChartData)
 *   - ALL logRef callbacks defined here — child components never touch logRef
 *   - activePatterns state lives here — same shape as activeIndicators
 *   - Drawer is position:absolute overlay (Fix 3) — never pushes chart
 *   - Height is flex:1 on container — useChartRenderer reads via ResizeObserver
 *
 * Signal chain rule:
 *   Every state change callback wraps: setState(newVal) + logRef.current?.(...)
 *   Child components receive opaque callbacks and call them — zero analytics knowledge.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

import apiFetch from '../utils/apiFetch';

import useChartRenderer from '../hooks/useChartRenderer';
import useChartData     from '../hooks/useChartData';
import Toolbar          from './chart/Toolbar';
import Sidebar          from './chart/Sidebar';
import DrawingToolbar   from './chart/DrawingToolbar';

import {
  API_URL, TABS, INDICATOR_REGISTRY, TEMPLATES, MODE_CONFIG,
  IND_COLORS, PANEL_MAX_MEMBER, PANEL_MAX_GUEST, OVERLAY_MAX_GUEST,
  getNextColor,
} from '../constants/chartConfig';
import { detectCombinationSignals } from '../lib/calcPatterns';
import { fmtPrice, fmtVol, getMarketStatus } from '../utils/chartUtils';
import { etFmt } from '../lib/chartScales';

// Must be after all imports — CI=true enforces import/first ESLint rule.
// Shadows global fetch so all calls in this module get automatic 401 token refresh.
const fetch = apiFetch; // eslint-disable-line no-unused-vars

// ── Component ─────────────────────────────────────────────────────────────────

export default function PriceChart({
  ticker,
  mode = 'fullscreen',
  isLoggedIn = false,
  token = null,
  user = null,
  initialTemplate = null,
  scanContext = null,
  onIndicatorChange = null,
}) {
  const modeConfig = MODE_CONFIG[mode] || MODE_CONFIG.fullscreen;

  // ── Core chart state ───────────────────────────────────────────────────────
  const [activeTab,         setActiveTab]         = useState(2); // default 3M
  const [chartType,         setChartType]         = useState('candlestick');
  const [intraRes,          setIntraRes]           = useState('5');
  const [showExtHours,      setShowExtHours]       = useState(false);
  const [renkoBrickOverride,setRenkoBrickOverride] = useState(null);

  // ── Indicator state ────────────────────────────────────────────────────────
  const [activeIndicators,  setActiveIndicators]  = useState([]);
  const [activeTemplate,    setActiveTemplate]    = useState(initialTemplate || null);

  // ── Pattern state (Fix 2) ──────────────────────────────────────────────────
  // Default empty — no patterns rendered until user opts in
  const [activePatterns,    setActivePatterns]    = useState([]);

  // ── Drawing state ──────────────────────────────────────────────────────────
  const [drawings,          setDrawings]          = useState([]);
  const [drawingMode,       setDrawingMode]       = useState(null);
  const [drawingHint,       setDrawingHint]       = useState(null);

  // ── UI state ───────────────────────────────────────────────────────────────
  const [drawerOpen,        setDrawerOpen]        = useState(false);
  const [sidebarCollapsed,  setSidebarCollapsed]  = useState(false);
  const [saveStatus,        setSaveStatus]        = useState('idle');
  const [alertPopover,      setAlertPopover]      = useState(null);
  const [drawerCategory,    setDrawerCategory]    = useState('All');

  // ── S27K state ─────────────────────────────────────────────────────────────
  const [analystData,       setAnalystData]       = useState(null);
  const [analystVisible,    setAnalystVisible]    = useState(false);
  const [priceAlerts,       setPriceAlerts]       = useState([]);

  // ── Analytics ──────────────────────────────────────────────────────────────
  const logRef = useRef(null);
  useEffect(() => {
    logRef.current = (eventType, extra) => {
      if (!ticker) return;
      fetch(`${API_URL}/api/interaction-logs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ticker, eventType, isLoggedIn: !!isLoggedIn, sessionHour: new Date().getHours(), marketStatus: getMarketStatus(), timeframe: TABS[activeTab]?.label, ...(scanContext ? { scanContext } : {}), ...(extra || {}) }),
      }).catch(() => {});
    };
  }, [ticker, token, isLoggedIn, activeTab, scanContext]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLoggedInRef = useRef(isLoggedIn);
  isLoggedInRef.current = isLoggedIn;

  // ── saveDrawingRef — stable ref to backend save fn ────────────────────────
  const saveDrawingRef = useRef(null);
  useEffect(() => {
    saveDrawingRef.current = async (data) => {
      if (!token) return null;
      try {
        const res = await fetch(`${API_URL}/api/drawings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ ticker, timeframe: TABS[activeTab]?.label, ...(scanContext ? { scanContext } : {}), ...data }),
        });
        if (!res.ok) return null;
        return (await res.json())._id || null;
      } catch { return null; }
    };
  }, [ticker, token, activeTab, scanContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── useChartRenderer — runs first, creates and returns all D3 refs ─────────
  const {
    containerRef,
    candlesRef, chartDataRef, tradingDatesRef, dateToIndexRef,
    xScaleRef, xScaleBaseRef, yScaleRef, zoomRef, gRef,
    innerWRef, innerHRef,
    wsRef, wsLiveRef, wsReconnectRef, wsTickerRef,
    showExtHoursRef, chartTypeRef,
    earnEventsRef, signalBadgesRef, candlePatternsRef, // bridge refs
    panelContainersRef,
    doRenderChart, doRenderPanels,
    ohlcv,
    legendValues, tooltipPos,
    crosshairLocked,
    showShortcuts, setShowShortcuts,
    ctxMenu, setCtxMenu,
  } = useChartRenderer({
    ticker,
    activeTab,   chartType,
    activeIndicators, activePatterns, comboSignals: [], // wired below after useMemo
    isLoggedIn,
    drawings,
    analystVisible, analystData, priceAlerts,
    mode,
    logRef, saveDrawingRef,
    setDrawings, setDrawingMode, setDrawingHint, setAlertPopover,
  });

  // ── useChartData — runs second, receives refs from renderer ────────────────
  const {
    loading, error,
    lastBar, priceChange,
    rsiValue, rsiPrev,
    renkoBrickAtr,
    signalBadges, candlePatterns,
    wsStatus,
  } = useChartData({
    ticker, activeTab, intraRes, showExtHours, chartType, renkoBrickOverride,
    activeIndicators, indicatorRegistry: INDICATOR_REGISTRY,
    candlesRef, chartDataRef, tradingDatesRef, dateToIndexRef,
    xScaleRef, xScaleBaseRef, yScaleRef, zoomRef, gRef,
    innerWRef, innerHRef,
    wsRef, wsLiveRef, wsReconnectRef, wsTickerRef,
    showExtHoursRef, chartTypeRef,
    earnEventsRef, signalBadgesRef, candlePatternsRef, // bridge refs
    doRenderChart, doRenderPanels,
    logRef,
  });

  // ── Keep showExtHoursRef in sync ───────────────────────────────────────────
  useEffect(() => { showExtHoursRef.current = showExtHours; }, [showExtHours]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Combination signals (derived from pattern + badge state) ──────────────
  // Computed AFTER useChartData provides signalBadges + candlePatterns.
  // comboSignalsRef in useChartRenderer is updated every render via ref mirror.
  // useEffect below triggers doRenderChart so the new signals paint immediately.
  const comboSignals = useMemo(() => {
    if (!candlePatterns.length && !signalBadges.length) return [];
    return detectCombinationSignals(chartDataRef.current, signalBadges, candlePatterns);
  }, [signalBadges, candlePatterns]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { doRenderChart(); }, [comboSignals]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentional: doRenderChart is stable ([] useCallback). comboSignals is the
  // only trigger needed — ref mirror in useChartRenderer is already updated.

  // ── URL state sync ─────────────────────────────────────────────────────────
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const tf = p.get('tf'), ct = p.get('ct'), tmpl = p.get('template');
    if (tf) { const i = TABS.findIndex(t => t.label.toLowerCase() === tf); if (i >= 0) setActiveTab(i); }
    if (ct) setChartType(ct);
    if (tmpl && TEMPLATES[tmpl]) applyTemplate(tmpl);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    p.set('tf', TABS[activeTab].label.toLowerCase());
    p.set('ct', chartType);
    if (activeTemplate) p.set('template', activeTemplate); else p.delete('template');
    window.history.replaceState(null, '', '?' + p.toString());
  }, [activeTab, chartType, activeTemplate]);

  // ── Layout persistence ─────────────────────────────────────────────────────
  const saveDebounce = useRef(null);
  useEffect(() => {
    if (!token || !ticker) return;
    setSaveStatus('saving');
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(() => {
      fetch(`${API_URL}/api/indicator-layouts/${ticker}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ activeIndicators, chartType, activeTemplate, sidebarCollapsed }),
      }).then(r => { setSaveStatus(r.ok ? 'saved' : 'error'); setTimeout(() => setSaveStatus('idle'), 3000); }).catch(() => setSaveStatus('error'));
    }, 1500);
  }, [activeIndicators, chartType, activeTemplate, sidebarCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load layout + drawings on ticker change ────────────────────────────────
  useEffect(() => {
    if (!ticker || !token) return;
    // Load saved layout
    fetch(`${API_URL}/api/indicator-layouts/${ticker}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(l => {
        if (l.activeIndicators) setActiveIndicators(l.activeIndicators);
        if (l.chartType)        setChartType(l.chartType);
        if (l.activeTemplate)   setActiveTemplate(l.activeTemplate);
        if (l.sidebarCollapsed != null) setSidebarCollapsed(l.sidebarCollapsed);
      }).catch(() => {});
    // Load saved drawings
    fetch(`${API_URL}/api/drawings/${ticker}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(data => { if (Array.isArray(data)) setDrawings(data); }).catch(() => {});
  }, [ticker]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── S27K: Analyst data + price alerts ─────────────────────────────────────
  useEffect(() => {
    if (!ticker) return;
    fetch(`${API_URL}/api/seo/${ticker}/analyst-target`)
      .then(r => r.json()).then(d => setAnalystData(d?.priceTarget ? d : null)).catch(() => {});
  }, [ticker]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!ticker || !token) return;
    fetch(`${API_URL}/api/alerts/${ticker}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (Array.isArray(d)) setPriceAlerts(d); }).catch(() => {});
  }, [ticker, token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Signal-logged callbacks ────────────────────────────────────────────────

  const handleTabChange = useCallback((i) => {
    setActiveTab(i);
    logRef.current?.('timeframe_change', { tab: TABS[i].label });
  }, []);

  const handleChartTypeChange = useCallback((key) => {
    setChartType(key);
    logRef.current?.('chart_type_change', { chartType: key });
  }, []);

  const handleIntraResChange = useCallback((r) => {
    setIntraRes(r);
    logRef.current?.('intraday_res_change', { resolution: r });
  }, []);

  const handleExtHoursToggle = useCallback(() => {
    setShowExtHours(v => { const n = !v; logRef.current?.('ext_hours_toggle', { enabled: n }); return n; });
  }, []);

  // Indicator add — enforces member/guest limits from chartConfig constants
  const handleAddIndicator = useCallback((key, params = {}) => {
    const entry = INDICATOR_REGISTRY[key]; if (!entry) return;
    const panelCount   = activeIndicators.filter(i => INDICATOR_REGISTRY[i.key]?.type === 'panel').length;
    const overlayCount = activeIndicators.filter(i => INDICATOR_REGISTRY[i.key]?.type === 'overlay').length;
    const panelMax     = isLoggedIn ? PANEL_MAX_MEMBER : PANEL_MAX_GUEST;
    if (entry.type === 'panel'   && panelCount   >= panelMax)       return;
    if (entry.type === 'overlay' && !isLoggedIn  && overlayCount >= OVERLAY_MAX_GUEST) return;
    const color      = getNextColor(key, activeIndicators);
    const instanceId = `${key}-${Date.now()}`;
    const newInd     = { key, params: { ...entry.defaultParams, ...params, color }, instanceId };
    setActiveIndicators(prev => [...prev, newInd]);
    // combinationHash — logs which indicators users run together (crowd signal).
    // Sorted so SMA20+EMA9 and EMA9+SMA20 produce the same hash. No vendor has this.
    const combinationHash = [...activeIndicators, newInd].map(i => i.key).sort().join('+');
    logRef.current?.('indicator_add', { indicatorKey: key, d3Signal: entry.d3Signal, combinationHash });
    onIndicatorChange?.([...activeIndicators, newInd]);
  }, [activeIndicators, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveIndicator = useCallback((instanceId) => {
    setActiveIndicators(prev => {
      const next = prev.filter(i => i.instanceId !== instanceId);
      logRef.current?.('indicator_remove', { instanceId });
      onIndicatorChange?.(next);
      return next;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Pattern add/remove
  const handleAddPattern = useCallback((key) => {
    const instanceId = `${key}-${Date.now()}`;
    setActivePatterns(prev => [...prev, { key, instanceId }]);
    logRef.current?.('pattern_add', { patternKey: key });
  }, []);

  const handleRemovePattern = useCallback((instanceId) => {
    setActivePatterns(prev => prev.filter(p => p.instanceId !== instanceId));
    logRef.current?.('pattern_remove', { instanceId });
  }, []);

  // Template apply
  const applyTemplate = useCallback((key) => {
    const tmpl = TEMPLATES[key]; if (!tmpl) return;
    const newInds = tmpl.indicators.map((t, i) => ({
      key: t.key,
      params: { ...INDICATOR_REGISTRY[t.key]?.defaultParams, ...t.params, color: (IND_COLORS[t.key] || ['#8b8ba7'])[i % (IND_COLORS[t.key]?.length || 1)] },
      instanceId: `${t.key}-tmpl-${i}-${Date.now()}`,
    }));
    setActiveIndicators(newInds);
    setActiveTemplate(key);
    logRef.current?.('template_apply', { template: key });
    onIndicatorChange?.(newInds);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drawing mode change
  const handleDrawingModeChange = useCallback((mode) => {
    setDrawingMode(mode);
    setDrawingHint(mode ? `Click chart to start ${mode} drawing` : null);
    if (mode) {
      logRef.current?.('drawing_tool_select', { drawingType: mode });
      // drawing_session_start — logs when user begins a drawing session.
      // Combined with drawingDwellRef in useChartRenderer, tracks time-on-task.
      logRef.current?.('drawing_session_start', { drawingType: mode, ts: Date.now() });
    }
  }, []);

  // Clear all drawings
  const handleClearDrawings = useCallback(async () => {
    setDrawings([]);
    logRef.current?.('drawings_clear');
    if (token) {
      try {
        await fetch(`${API_URL}/api/drawings/${ticker}/all`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      } catch {}
    }
  }, [ticker, token]);

  // Analyst toggle
  const handleAnalystToggle = useCallback(() => {
    setAnalystVisible(v => {
      const next = !v;
      if (next && analystData) logRef.current?.('analyst_marker_view', { priceTarget: analystData.priceTarget, consensus: analystData.consensus });
      return next;
    });
  }, [analystData]);

  // Save price alert
  const handleSaveAlert = useCallback(async () => {
    if (!alertPopover || !token) return;
    setAlertPopover(prev => ({ ...prev, saving: true, error: null }));
    try {
      const res = await fetch(`${API_URL}/api/alerts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ticker, targetPrice: alertPopover.price, direction: alertPopover.direction }),
      });
      if (!res.ok) throw new Error();
      const saved = await res.json();
      setPriceAlerts(prev => [...prev, saved]);
      setAlertPopover(null);
      logRef.current?.('alert_set', { targetPrice: alertPopover.price, direction: alertPopover.direction });
    } catch { setAlertPopover(prev => ({ ...prev, saving: false, error: 'Save failed' })); }
  }, [alertPopover, token, ticker]);

  const handleCancelAlert = useCallback(async (alertId) => {
    setPriceAlerts(prev => prev.filter(a => a._id !== alertId));
    if (!token) return;
    try { await fetch(`${API_URL}/api/alerts/${alertId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }); } catch {}
  }, [token]);

  // Screenshot (S27QW-D)
  const handleScreenshot = useCallback(() => {
    const svgEl = containerRef.current?.querySelector('svg.mc'); if (!svgEl) return;
    const { width: w, height: h } = svgEl.getBoundingClientRect();
    const clone = svgEl.cloneNode(true);
    clone.querySelectorAll('[style*="font-family"]').forEach(el => el.style.fontFamily = 'Arial, sans-serif');
    const svgStr  = new XMLSerializer().serializeToString(clone);
    const svgFixed = svgStr.replace(/&nbsp;/g, ' ');
    const b64 = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgFixed)));
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h + 24;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#050d1a'; ctx.fillRect(0, 0, w, h + 24);
      ctx.drawImage(img, 0, 0, w, h);
      ctx.fillStyle = 'rgba(0,0,0,0.6)'; ctx.fillRect(0, h, w, 24);
      ctx.fillStyle = '#00d4ff'; ctx.font = 'bold 11px Arial'; ctx.fillText('✦ MarketPulze', 10, h + 16);
      ctx.fillStyle = '#4a5568'; ctx.font = '10px Arial'; ctx.fillText(`${ticker} · ${TABS[activeTab]?.label} · ${new Date().toLocaleDateString()}`, 120, h + 16);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `${ticker}_${TABS[activeTab]?.label}_marketpulze.png`;
      a.click();
    };
    img.src = b64;
    logRef.current?.('screenshot', { ticker, timeframe: TABS[activeTab]?.label });
  }, [activeTab, ticker, containerRef]);

  // Derived
  const panelInds = activeIndicators.filter(i => INDICATOR_REGISTRY[i.key]?.type === 'panel');
  const isRenko   = chartType === 'renko';

  // Filtered drawer indicator list
  const drawerList = Object.entries(INDICATOR_REGISTRY).filter(([, entry]) =>
    drawerCategory === 'All' || entry.type === (drawerCategory === 'Overlays' ? 'overlay' : 'panel')
  );

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div
      ref={el => {
        // Measure actual top offset after mount and apply dynamic height.
        // This replaces the hardcoded calc(100vh - 64px) which was wrong when
        // the breadcrumb row or any other chrome above the chart has variable height.
        if (el && (mode === 'fullscreen' || mode === 'tab')) {
          requestAnimationFrame(() => {
            const top = el.getBoundingClientRect().top;
            if (top > 0) el.style.height = `${window.innerHeight - top}px`;
          });
        }
      }}
      style={{
        width: '100%',
        height: (mode === 'fullscreen' || mode === 'tab') ? 'calc(100vh - 96px)' : '100%',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        background: '#050d1a', color: '#e2e8f0', fontFamily: 'inherit', userSelect: 'none',
      }}>

      {/* Toolbar */}
      <Toolbar
        activeTab={activeTab}           onTabChange={handleTabChange}
        chartType={chartType}           onChartTypeChange={handleChartTypeChange}
        intraRes={intraRes}             onIntraResChange={handleIntraResChange}
        showExtHours={showExtHours}     onExtHoursToggle={handleExtHoursToggle}
        isLoggedIn={isLoggedIn}
        isRenko={isRenko}               renkoBrickAtr={renkoBrickAtr}
        renkoBrickOverride={renkoBrickOverride} onRenkoBrickChange={setRenkoBrickOverride}
        lastBar={lastBar}               priceChange={priceChange}
        rsiValue={rsiValue}             rsiPrev={rsiPrev}
        wsStatus={wsStatus}             crosshairLocked={crosshairLocked}
        analystData={analystData}       analystVisible={analystVisible}
        onAnalystToggle={handleAnalystToggle}
        onScreenshot={handleScreenshot}
        modeConfig={modeConfig}
      />

      {/* OHLCV bar */}
      {ohlcv && (
        <div style={{ display: 'flex', gap: 8, padding: '1px 12px', fontSize: 10, color: '#6b7280', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
          {ohlcv.date && <span style={{ color: '#00d4ff', fontWeight: 700, marginRight: 4, borderRight: '1px solid rgba(255,255,255,0.08)', paddingRight: 8 }}>{etFmt(ohlcv.date, activeTab)}</span>}
          <span>O <span style={{ color: '#e2e8f0' }}>{fmtPrice(ohlcv.open)}</span></span>
          <span>H <span style={{ color: '#10b981' }}>{fmtPrice(ohlcv.high)}</span></span>
          <span>L <span style={{ color: '#ef4444' }}>{fmtPrice(ohlcv.low)}</span></span>
          <span>C <span style={{ color: ohlcv.close >= ohlcv.open ? '#10b981' : '#ef4444' }}>{fmtPrice(ohlcv.close)}</span></span>
          <span>Vol <span style={{ color: '#8b8ba7' }}>{fmtVol(ohlcv.volume)}</span></span>
          {ohlcv.open > 0 && (() => { const chg = ohlcv.close - ohlcv.open, pct = (chg / ohlcv.open) * 100, pos = chg >= 0; return (<span style={{ padding: '1px 5px', borderRadius: 3, fontSize: 9, fontWeight: 700, background: pos ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)', color: pos ? '#10b981' : '#ef4444' }}>{pos ? '+' : ''}{pct.toFixed(2)}%</span>); })()}
          {Object.values(legendValues).map((lv, i) => (
            <span key={i} style={{ fontSize: 10, color: lv.color }}>{lv.label}: {lv.value}</span>
          ))}
        </div>
      )}

      {/* Drawing hint */}
      {drawingHint && <div style={{ padding: '2px 12px', fontSize: 10, color: '#00d4ff', opacity: 0.8 }}>{drawingHint}</div>}

      {/* Main area — DrawingToolbar LEFT + chart canvas + sidebar */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', position: 'relative' }}>

        {/* Left drawing toolbar — vertical, zero height cost */}
        <DrawingToolbar
          drawingMode={drawingMode}
          onDrawingModeChange={handleDrawingModeChange}
          onClearDrawings={handleClearDrawings}
        />

        {/* Chart canvas — flex:1, ResizeObserver reads actual height */}
        <div style={{ flex: 1, minWidth: 0, position: 'relative' }}>
          {loading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, background: 'rgba(5,13,26,0.7)' }}><span style={{ fontSize: 12, color: '#00d4ff' }}>Loading…</span></div>}
          {error   && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10 }}><span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span></div>}

          {/* D3 target div — containerRef attached here */}
          <div ref={containerRef} tabIndex={0}
            onMouseEnter={() => containerRef.current?.focus()}
            style={{ width: '100%', height: '100%', cursor: drawingMode ? 'crosshair' : 'default', outline: 'none' }} />

          {/* Floating OHLCV tooltip (S27FIX3) */}
          {ohlcv && (
            <div style={{ position: 'absolute', left: Math.min(tooltipPos.x + 14, (containerRef.current?.clientWidth || 800) - 165), top: Math.max(tooltipPos.y - 75, 4), pointerEvents: 'none', zIndex: 30, background: 'rgba(10,16,35,0.92)', border: '1px solid rgba(0,212,255,0.18)', borderRadius: 6, padding: '5px 9px', fontSize: 11, lineHeight: 1.7, backdropFilter: 'blur(4px)', boxShadow: '0 4px 16px rgba(0,0,0,0.4)' }}>
              {ohlcv.date && <div style={{ color: '#00d4ff', fontWeight: 700, fontSize: 10, borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 4, marginBottom: 4 }}>{etFmt(ohlcv.date, activeTab)}</div>}
              <div style={{ display: 'flex', gap: 10 }}><span style={{ color: '#4a5568' }}>O</span><span style={{ color: '#e2e8f0' }}>{fmtPrice(ohlcv.open)}</span><span style={{ color: '#4a5568' }}>H</span><span style={{ color: '#10b981' }}>{fmtPrice(ohlcv.high)}</span></div>
              <div style={{ display: 'flex', gap: 10 }}><span style={{ color: '#4a5568' }}>L</span><span style={{ color: '#ef4444' }}>{fmtPrice(ohlcv.low)}</span><span style={{ color: '#4a5568' }}>C</span><span style={{ color: ohlcv.close >= ohlcv.open ? '#10b981' : '#ef4444' }}>{fmtPrice(ohlcv.close)}</span></div>
              <div style={{ color: '#4a5568', fontSize: 10 }}>Vol <span style={{ color: '#8b8ba7' }}>{fmtVol(ohlcv.volume)}</span></div>
              {Object.keys(legendValues).length > 0 && (
                <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 4, paddingTop: 4 }}>
                  <div style={{ fontSize: 8, color: '#334155', marginBottom: 3 }}>AT THIS CANDLE</div>
                  {Object.values(legendValues).map((lv, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 10 }}>
                      <span style={{ color: 'rgba(255,255,255,0.3)', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: lv.color, display: 'inline-block' }} />{lv.label}
                      </span>
                      <span style={{ color: lv.color, fontWeight: 600 }}>${lv.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Indicator drawer — Fix 3: position:absolute overlay, never pushes chart */}
          {drawerOpen && modeConfig.pills && (
            <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 260, zIndex: 50, background: 'rgba(5,13,26,0.97)', border: '1px solid rgba(0,212,255,0.18)', borderRadius: '8px 8px 0 0', display: 'flex', overflow: 'hidden' }}>
              {/* Available indicators */}
              <div style={{ flex: 1, padding: '8px 10px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <span style={{ fontSize: 10, color: '#00d4ff', fontWeight: 700 }}>AVAILABLE — click + to add</span>
                  <div style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
                    {['All','Overlays','Oscillators'].map(cat => (
                      <button key={cat} onClick={() => setDrawerCategory(cat)}
                        style={{ padding: '2px 8px', borderRadius: 4, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, cursor: 'pointer', border: `1px solid ${drawerCategory === cat ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.08)'}`, background: drawerCategory === cat ? 'rgba(0,212,255,0.1)' : 'transparent', color: drawerCategory === cat ? '#00d4ff' : '#6b7280' }}>
                        {cat}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => setDrawerOpen(false)}
                    style={{ marginLeft: 'auto', width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#6b7280', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {drawerList.map(([key, entry]) => (
                    <div key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, padding: '4px 0' }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: (IND_COLORS[key] || ['#8b8ba7'])[0], flexShrink: 0, marginTop: 3 }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0' }}>{entry.label}{entry.note ? <span style={{ fontSize: 9, color: '#6b7280', marginLeft: 4 }}>{entry.note}</span> : null}</div>
                        <div style={{ fontSize: 10, color: '#6b7280' }}>{entry.desc}</div>
                      </div>
                      <button onClick={() => handleAddIndicator(key)}
                        style={{ width: 20, height: 20, borderRadius: 4, background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', color: '#00d4ff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>+</button>
                    </div>
                  ))}
                </div>
              </div>
              {/* Active indicators in drawer */}
              <div style={{ width: 200, borderLeft: '1px solid rgba(255,255,255,0.08)', padding: '8px 10px', overflowY: 'auto' }}>
                <div style={{ fontSize: 10, color: '#4a4a6a', fontWeight: 700, marginBottom: 6 }}>ACTIVE — edit params · dot = color</div>
                {activeIndicators.length === 0 && <div style={{ fontSize: 10, color: '#4a4a6a' }}>No indicators active</div>}
                {activeIndicators.map(ind => {
                  const entry = INDICATOR_REGISTRY[ind.key]; if (!entry) return null;
                  const color = ind.params?.color || (IND_COLORS[ind.key] || ['#8b8ba7'])[0];
                  return (
                    <div key={ind.instanceId} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
                      <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0, cursor: 'pointer' }} />
                      <span style={{ flex: 1, fontSize: 10, color: '#e2e8f0' }}>{entry.label}{ind.params?.period ? ` (${ind.params.period})` : ''}</span>
                      <button onClick={() => handleRemoveIndicator(ind.instanceId)}
                        style={{ background: 'none', border: 'none', color: '#4a4a6a', cursor: 'pointer', fontSize: 13, padding: 0 }}>×</button>
                    </div>
                  );
                })}
                <div style={{ fontSize: 10, color: '#4a4a6a', fontWeight: 700, margin: '8px 0 4px' }}>TEMPLATES</div>
                {Object.entries(TEMPLATES).map(([key, tmpl]) => (
                  <button key={key} onClick={() => applyTemplate(key)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 4, fontFamily: 'inherit', fontSize: 10, cursor: 'pointer', marginRight: 4, marginBottom: 3, border: `1px solid ${activeTemplate === key ? tmpl.color : 'rgba(255,255,255,0.08)'}`, background: activeTemplate === key ? tmpl.color + '20' : 'transparent', color: activeTemplate === key ? tmpl.color : '#6b7280' }}>
                    {tmpl.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Keyboard shortcut overlay */}
          {showShortcuts && (
            <div onClick={() => setShowShortcuts(false)} style={{ position: 'absolute', inset: 0, zIndex: 200, background: 'rgba(5,13,26,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <div onClick={e => e.stopPropagation()} style={{ background: 'rgba(10,16,35,0.98)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: 10, padding: '20px 28px', minWidth: 300, cursor: 'default' }}>
                <div style={{ color: '#00d4ff', fontWeight: 700, fontSize: 14, marginBottom: 14 }}>⌨ Keyboard Shortcuts</div>
                {[['← →', 'Navigate candles'], ['L', 'Lock/unlock crosshair'], ['?', 'Show this overlay'], ['Esc', 'Close overlays']].map(([k, d]) => (
                  <div key={k} style={{ display: 'flex', gap: 16, marginBottom: 8, fontSize: 12 }}>
                    <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 4, color: '#00d4ff', minWidth: 40, textAlign: 'center' }}>{k}</code>
                    <span style={{ color: '#8b8ba7' }}>{d}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Context menu (right-click) */}
          {ctxMenu && (
            <div style={{ position: 'absolute', left: ctxMenu.x, top: ctxMenu.y, zIndex: 100, background: 'rgba(10,16,35,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, minWidth: 160, padding: '4px 0', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
              <div style={{ fontSize: 9, color: '#4a4a6a', padding: '2px 10px', marginBottom: 2 }}>{fmtPrice(ctxMenu.price)}</div>
              {[
                ['Set Alert', () => { setAlertPopover({ price: Math.round(ctxMenu.price * 100) / 100, direction: 'above', saving: false, error: null }); setCtxMenu(null); }, isLoggedIn],
                ['Draw Level', () => { setDrawingMode('horizontal'); setCtxMenu(null); }, true],
                ['Draw Fib from here', () => { setDrawingMode('fibonacci'); setCtxMenu(null); }, true],
                ['Anchor VWAP here', () => { setDrawingMode('anchored_vwap'); setCtxMenu(null); }, true],
              ].filter(([, , show]) => show).map(([label, fn]) => (
                <button key={label} onClick={fn}
                  style={{ display: 'block', width: '100%', textAlign: 'left', padding: '5px 12px', background: 'none', border: 'none', color: '#e2e8f0', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}
                  onMouseEnter={e => e.target.style.background = 'rgba(0,212,255,0.08)'}
                  onMouseLeave={e => e.target.style.background = 'none'}>
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        {modeConfig.sidebar && (
          <Sidebar
            activeIndicators={activeIndicators}
            onAddIndicator={handleAddIndicator}
            onRemoveIndicator={handleRemoveIndicator}
            activePatterns={activePatterns}
            onAddPattern={handleAddPattern}
            onRemovePattern={handleRemovePattern}
            comboSignals={comboSignals}
            activeTemplate={activeTemplate}
            onApplyTemplate={applyTemplate}
            collapsed={sidebarCollapsed}
            onCollapseToggle={() => setSidebarCollapsed(v => !v)}
            isLoggedIn={isLoggedIn}
            panelContainersRef={panelContainersRef}
            priceAlerts={priceAlerts}
            onCancelAlert={handleCancelAlert}
          />
        )}
      </div>

      {/* Sub-panels (RSI, MACD) — D3 paints into panelContainersRef divs */}
      {modeConfig.panels && panelInds.map(ind => (
        <div key={ind.instanceId}
          ref={el => { panelContainersRef.current[ind.instanceId] = el; }}
          style={{ width: '100%', borderTop: '1px solid rgba(255,255,255,0.06)' }} />
      ))}

      {/* Indicators button — opens/closes overlay drawer */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '2px 12px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <button onClick={() => setDrawerOpen(o => !o)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 12px', borderRadius: 20, background: drawerOpen ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.05)', border: `1px solid ${drawerOpen ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.12)'}`, color: drawerOpen ? '#00d4ff' : '#6b7280', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s' }}>
          + Indicators
        </button>
        {saveStatus === 'saving' && <span style={{ fontSize: 9, color: '#4a4a6a' }}>Saving layout…</span>}
        {saveStatus === 'saved'  && <span style={{ fontSize: 9, color: '#10b981' }}>Layout saved</span>}
      </div>

      {/* Price alert popover (S27K) */}
      {alertPopover && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 300, background: 'rgba(10,16,35,0.98)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 8, padding: '12px 16px', minWidth: 220, boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}>
          <div style={{ fontSize: 11, color: '#8b8ba7', marginBottom: 8 }}>
            Set alert at <strong style={{ color: '#e2e8f0' }}>{fmtPrice(alertPopover.price)}</strong>
          </div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {['above','below'].map(dir => (
              <button key={dir} onClick={() => setAlertPopover(prev => ({ ...prev, direction: dir }))}
                style={{ flex: 1, padding: '4px 0', borderRadius: 5, fontFamily: 'inherit', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: `1px solid ${alertPopover.direction === dir ? (dir === 'above' ? 'rgba(16,185,129,0.6)' : 'rgba(239,68,68,0.6)') : 'rgba(255,255,255,0.1)'}`, background: alertPopover.direction === dir ? (dir === 'above' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)') : 'transparent', color: alertPopover.direction === dir ? (dir === 'above' ? '#10b981' : '#ef4444') : '#6b7280' }}>
                {dir === 'above' ? '▲ Above' : '▼ Below'}
              </button>
            ))}
          </div>
          {alertPopover.error && <div style={{ fontSize: 10, color: '#ef4444', marginBottom: 6 }}>{alertPopover.error}</div>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={handleSaveAlert} disabled={alertPopover.saving}
              style={{ flex: 1, padding: '5px 0', borderRadius: 5, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: alertPopover.saving ? 'not-allowed' : 'pointer', border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.15)', color: '#a78bfa', opacity: alertPopover.saving ? 0.6 : 1 }}>
              {alertPopover.saving ? 'Saving…' : 'Save Alert'}
            </button>
            <button onClick={() => setAlertPopover(null)}
              style={{ padding: '5px 10px', borderRadius: 5, fontFamily: 'inherit', fontSize: 11, cursor: 'pointer', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#6b7280' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
